import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Send, Loader2, ArrowRight } from "lucide-react";
import { API } from "../config";

const IDAIRA_AVATAR = "https://static.prod-images.emergentagent.com/jobs/7b94a5c1-823e-479c-ba29-68ed8b8691d3/images/d7ff3b903b7e0317ea9bf8140c4c2aea957b9302e221d2fd151be044ae806f4c.jpeg";

const getSessionId = () => {
  let sid = localStorage.getItem("lw_chat_sid");
  if (!sid) {
    sid = `web-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem("lw_chat_sid", sid);
  }
  return sid;
};

const WELCOME = { role: "assistant", content: "¡Hola! 👋 Soy Idaira, la asistente virtual de LiveWifi. Puedo ayudarte con tarifas de fibra y móvil, eSIMs de viaje y algunas cosas más.\n\n¿En qué te ayudo?" };

const LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^\s)]+|(?:www\.)?livewifi\.es\/[^\s)]*)\)|(https?:\/\/[^\s<]+|(?:www\.)?livewifi\.es(?:\/[^\s<]*)?)/gi;

const PRICE_PAGES = {
  "/fibra-movil": "Fibra + Fijo + Móvil",
  "/fibra-fijo": "Fibra + Fijo",
  "/tarifas-movil": "Solo Móvil",
  "/empresas": "Empresas",
  "/tv": "LiveWifi TV",
  "/internet-rural": "Internet Rural",
  "/travel": "eSIMs de viaje",
};

const toHref = (raw) => {
  const url = raw.replace(/[.,;:!?)·]+$/, "");
  const m = url.match(/^(?:https?:\/\/)?(?:www\.)?livewifi\.es(\/.*)?$/i);
  if (m) return { href: m[1] || "/", internal: true, trimmed: url, trail: raw.slice(url.length) };
  return { href: url, internal: false, trimmed: url, trail: raw.slice(url.length) };
};

const renderWithLinks = (text) => {
  if (!text) return text;
  const parts = [];
  let last = 0;
  for (const m of text.matchAll(LINK_RE)) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const label = m[1];
    const { href, internal, trimmed, trail } = toHref(m[2] || m[3]);
    const priceLabel = internal ? PRICE_PAGES[href.replace(/\/$/, "").split(/[?#]/)[0]] : null;
    parts.push(
      priceLabel ? (
        <a key={m.index} href={href} data-testid="chat-price-btn"
          className="inline-flex items-center gap-1.5 mx-0.5 my-1 px-3 py-1.5 rounded-full bg-[#7DD500] text-black text-xs font-bold no-underline hover:bg-[#9BE82A] transition-colors align-middle">
          Ver tarifa · {priceLabel} <ArrowRight className="w-3 h-3" />
        </a>
      ) : (
        <a key={m.index} href={href} target={internal ? "_self" : "_blank"} rel="noopener noreferrer" data-testid="chat-link"
          className="text-[#7DD500] underline underline-offset-2 hover:text-[#9BE82A] break-all">
          {label || trimmed}
        </a>
      )
    );
    if (!label && trail) parts.push(trail);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};


const AIChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [needsAgent, setNeedsAgent] = useState(false);
  const location = useLocation();
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const sendingRef = useRef(false);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!open || loaded) return;
    setLoaded(true);
    fetch(`${API}/chat/history/${getSessionId()}`)
      .then(r => r.json())
      .then(d => { if (d.messages?.length) setMessages([WELCOME, ...d.messages.map(m => ({ role: m.role, content: m.content }))]); })
      .catch(() => {});
    fetch(`${API}/chat/session/${getSessionId()}/status`)
      .then(r => r.json())
      .then(d => setNeedsAgent(!!d.needs_agent))
      .catch(() => {});
  }, [open, loaded]);

  // Poll for new agent messages while transferred to a human agent (replaces full thread, skipped mid-stream)
  useEffect(() => {
    if (!needsAgent || !open) return;
    const poll = () => {
      if (sendingRef.current) return;
      fetch(`${API}/chat/history/${getSessionId()}`)
        .then(r => r.json())
        .then(d => {
          if (sendingRef.current) return;
          if (d.messages?.length) setMessages([WELCOME, ...d.messages.map(m => ({ role: m.role, content: m.content }))]);
        })
        .catch(() => {});
    };
    const interval = setInterval(poll, 4000);
    return () => clearInterval(interval);
  }, [needsAgent, open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    sendingRef.current = true;
    setMessages(prev => [...prev, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          let d;
          try { d = JSON.parse(p.slice(6)); } catch { continue; }
          if (d.delta) {
            setMessages(prev => {
              const next = [...prev];
              next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + d.delta };
              return next;
            });
          }
          if (d.error) {
            setMessages(prev => {
              const next = [...prev];
              next[next.length - 1] = { role: "assistant", content: d.error };
              return next;
            });
          }
          if (d.handoff) {
            setNeedsAgent(true);
          }
        }
      }
    } catch {
      setMessages(prev => {
        const next = [...prev];
        next[next.length - 1] = { role: "assistant", content: "No he podido responder ahora mismo. Escríbenos por WhatsApp al 601 651 014 📱" };
        return next;
      });
    } finally {
      setSending(false);
      sendingRef.current = false;
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  return (
    <div className="fixed bottom-6 right-24 z-[60]" data-testid="ai-chat-widget">
      {open && (
        <div
          className="fixed sm:absolute bottom-24 sm:bottom-20 left-4 right-4 sm:left-auto sm:right-0 w-auto sm:w-[370px] max-w-[calc(100vw-2rem)] rounded-2xl overflow-hidden shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6)] border border-[#7DD500]/20 bg-[#0A0A0A]"
          style={{ animation: "ai-slide-up 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)" }}
          data-testid="ai-chat-panel"
        >
          <div className="bg-gradient-to-br from-[#1a2e05] to-[#0f1c03] p-4 relative border-b border-[#7DD500]/20">
            <button onClick={() => setOpen(false)} className="absolute top-3 right-3 text-white/70 hover:text-white" data-testid="ai-chat-close" aria-label="Cerrar chat IA">
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-[#7DD500]/40">
                  <img src={IDAIRA_AVATAR} alt="Idaira" className={`w-full h-full object-cover transition-transform ${sending ? "idaira-typing" : ""}`} />
                </div>
                <span className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full bg-[#7DD500] border-2 border-[#0f1c03]" />
              </div>
              <div>
                <div className="text-white font-bold text-base">Idaira · LiveWifi</div>
                <div className="text-[#7DD500] text-xs">IA · Disponible 24/7</div>
              </div>
            </div>
          </div>

          <div ref={scrollRef} className="p-3 h-[45vh] max-h-[340px] sm:h-[340px] overflow-y-auto space-y-3" data-testid="ai-chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#7DD500] text-black rounded-br-sm font-medium" : "bg-white/[0.06] text-white/90 border border-white/5 rounded-bl-sm"}`}>
                  {m.content ? (m.role === "assistant" ? renderWithLinks(m.content) : m.content) : (sending && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin text-[#7DD500]" /> : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-white/10">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && send()}
                placeholder="Escribe tu pregunta..."
                disabled={sending}
                data-testid="ai-chat-input"
                className="flex-1 px-3.5 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-white/30 focus:border-[#7DD500]/50 outline-none disabled:opacity-50"
              />
              <button
                onClick={send}
                disabled={!input.trim() || sending}
                data-testid="ai-chat-send"
                aria-label="Enviar mensaje"
                className="w-10 h-10 rounded-xl bg-[#7DD500] text-black flex items-center justify-center hover:bg-[#8DF000] transition-colors disabled:opacity-40 shrink-0"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-center text-[10px] text-gray-600 mt-2">Respuestas generadas por IA · Para gestiones usa WhatsApp 601 651 014</p>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen(v => !v)}
        data-testid="ai-chat-float-btn"
        aria-label={open ? "Cerrar Idaira" : "Abrir Idaira, el asistente IA"}
        className="relative w-16 h-16 bg-[#0f1c03] border-2 border-[#7DD500] rounded-full flex items-center justify-center shadow-lg hover:scale-110 active:scale-95 transition-transform hover:shadow-[0_0_30px_rgba(125,213,0,0.5)]"
      >
        {open ? <X className="w-7 h-7 text-[#7DD500]" /> : (
          <div className="w-[52px] h-[52px] rounded-full overflow-hidden">
            <img src={IDAIRA_AVATAR} alt="Idaira" loading="lazy" width="40" height="40" className="w-full h-full object-cover" />
          </div>
        )}
        {!open && (
          <span className="absolute -top-1 -right-1 px-1.5 py-0.5 bg-[#7DD500] text-black text-[9px] font-black rounded-full border-2 border-[#0A0A0A]">IA</span>
        )}
      </button>

      <style>{`
        @keyframes ai-slide-up {
          from { opacity: 0; transform: translateY(16px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes idaira-wink {
          0%, 100% { transform: scale(1) scaleY(1); filter: brightness(1); }
          45% { transform: scale(1.06) scaleY(1); filter: brightness(1.2); }
          50% { transform: scale(1.06) scaleY(0.85); filter: brightness(1.3); }
          55% { transform: scale(1.06) scaleY(1); filter: brightness(1.2); }
        }
        .idaira-typing { animation: idaira-wink 1.1s ease-in-out infinite; transform-origin: center 40%; }
      `}</style>
    </div>
  );
};

export default AIChatWidget;
